import React from "react";
import { useAuth } from "../lib/auth";
import { ShieldAlert, LogOut } from "lucide-react";

/**
 * Impersonation banner — rendered only when the authenticated user's JWT
 * carries an `imp=true` claim (super-admin currently signed in as a tenant).
 *
 * Visually loud on purpose: amber background, dark border, sticky to the
 * top of the dashboard viewport. The goal is to make it impossible for the
 * super-admin to forget they're operating inside a tenant context.
 *
 * "Exit Impersonation" restores the original super-admin token saved in
 * `sa_token_backup`, refreshes the user via /auth/me, and navigates back to
 * the internal tenants list. The short-lived impersonation JWT (30 min)
 * naturally invalidates itself if the user closes the tab.
 */
export default function ImpersonationBanner() {
  const { user } = useAuth();

  if (!user || !user.imp) return null;

  const companyName = (typeof window !== "undefined" && localStorage.getItem("impersonation_company")) || "this tenant";
  const impersonator = user.imp_name || "Super Admin";

  const exit = () => {
    const backup = localStorage.getItem("sa_token_backup");
    localStorage.removeItem("impersonation_company");
    localStorage.removeItem("sa_token_backup");
    if (backup) {
      localStorage.setItem("ren_token", backup);
    } else {
      // No backup → fall back to a clean logout so we never strand the
      // operator inside a stale impersonation session.
      localStorage.removeItem("ren_token");
    }
    // Hard reload to /internal-admin/tenants so AuthProvider re-bootstraps
    // with the restored super-admin token (soft-nav would race with the
    // Protected gate that checks isInternalRole on stale React state).
    window.location.replace("/internal-admin/tenants");
  };

  return (
    <div
      data-testid="impersonation-banner"
      role="alert"
      aria-live="assertive"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 60,
        background: "repeating-linear-gradient(135deg, #f59e0b 0 18px, #fbbf24 18px 36px)",
        borderBottom: "3px solid #78350f",
        boxShadow: "0 2px 8px rgba(120, 53, 15, 0.25)",
        color: "#1c1917",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          maxWidth: "100%",
          padding: "10px 16px",
          fontWeight: 600,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flexWrap: "wrap" }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "3px 9px",
              borderRadius: 999,
              background: "#78350f",
              color: "#fff7ed",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              flexShrink: 0,
            }}
          >
            <ShieldAlert className="w-3.5 h-3.5" /> Impersonation
          </span>
          <span style={{ fontSize: 14, color: "#1c1917" }}>
            Viewing as Tenant: <strong data-testid="impersonation-company-name">{companyName}</strong>
          </span>
          <span style={{ fontSize: 12, color: "#44403c", fontWeight: 500 }}>
            (super-admin {impersonator} · changes apply to this tenant)
          </span>
        </div>
        <button
          type="button"
          onClick={exit}
          data-testid="exit-impersonation"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "#1c1917",
            color: "#fef3c7",
            padding: "7px 14px",
            borderRadius: 8,
            border: "none",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = "#000")}
          onMouseOut={(e) => (e.currentTarget.style.background = "#1c1917")}
        >
          <LogOut className="w-3.5 h-3.5" />
          Exit Impersonation
        </button>
      </div>
    </div>
  );
}
